import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IndexTable,
    IndexFilters,
    useSetIndexFiltersMode,
    Text,
    useBreakpoints,
    Thumbnail,
    Button,
    Tooltip,
} from '@shopify/polaris';
import { ExternalSmallIcon } from '@shopify/polaris-icons';
import { useDispatch } from 'react-redux';
import { handleCollection } from '../../redux/product/slice';
import { dynamicResourceName, redirectToAdmin, limit } from '../../utils';

const CollectionTable = ({
    collections,
    pageInfo,
    totalCollectionsCount,
    onPaginationChange,
    isCollectionLoading,
    handleFiltersQueryChange,
    handleQueryValueRemove,
    searchValue,
    selectedResources,
    allResourcesSelected,
    handleSelectionChange
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selected, setSelected] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const { mode, setMode } = useSetIndexFiltersMode();

    const handleNavigate = (id, title, image, productsCount) => {
        navigate(`/configure-products/${id.split('/').pop()}`);
        dispatch(handleCollection({
            title: title,
            image: image,
            productsCount: productsCount
        }));
    }

    const handleNext = useCallback(() => {
        if (pageInfo.hasNextPage && onPaginationChange) {
            setCurrentPage(prev => prev + 1);
            onPaginationChange({
                after: pageInfo.endCursor,
                before: null,
                query: searchValue
            });
        }
    }, [pageInfo, onPaginationChange, searchValue]);

    const handlePrevious = useCallback(() => {
        if (pageInfo.hasPreviousPage && onPaginationChange) {
            setCurrentPage(prev => prev - 1);
            onPaginationChange({
                after: null,
                before: pageInfo.startCursor,
                query: searchValue
            });
        }
    }, [pageInfo, onPaginationChange, searchValue]);

    const rowMarkup =  collections.length > 0 ? collections.map(
        (
            { id, title, image, productsCount },
            index,
        ) => {
            return (
                <IndexTable.Row
                    id={id}
                    key={id}
                    selected={selectedResources.includes(id)}
                    position={index}
                >
                    <IndexTable.Cell>
                        <Thumbnail size="small" source={image} />
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <div
                            // onClick={() => handleNavigate(id, title, image, productsCount)}
                            style={{ cursor: 'pointer' }}
                        >
                            <Text variant="bodyMd" fontWeight="bold" as="span">
                                {id.split('/').pop()}
                            </Text>
                        </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        {title}
                    </IndexTable.Cell>
                    <IndexTable.Cell>{productsCount?.count || 0} products</IndexTable.Cell>
                    <IndexTable.Cell>
                        <Tooltip content='View collection'>
                            <Button
                                onClick={() => redirectToAdmin('collections', null, null, id.split('/').pop())}
                                icon={ExternalSmallIcon}
                                variant='secondary'
                            ></Button>
                        </Tooltip>
                    </IndexTable.Cell>
                </IndexTable.Row>
            );
        }
    ) : null;

    return (
        <React.Fragment>
            <IndexFilters
                queryValue={searchValue}
                queryPlaceholder="Searching in all"
                onQueryChange={handleFiltersQueryChange}
                onQueryClear={handleQueryValueRemove}
                cancelAction={{
                    onAction: handleQueryValueRemove,
                    disabled: false,
                    loading: false,
                }}
                tabs={[]}
                selected={selected}
                onSelect={setSelected}
                canCreateNewView={false}
                onCreateNewView={() => { }}
                filters={[]}
                appliedFilters={[]}
                onClearAll={handleQueryValueRemove}
                mode={mode}
                setMode={setMode}
            />
            <div className='table'>
                <IndexTable
                    condensed={useBreakpoints().smDown}
                    resourceName={dynamicResourceName('collection')}
                    itemCount={collections.length}
                    selectedItemsCount={
                        allResourcesSelected ? 'All' : selectedResources.length
                    }
                    onSelectionChange={(selectionType, toggleType, selection) => {
                        if (selectedResources?.length > 0 && selection !== selectedResources[0]) return;
                        handleSelectionChange(selectionType, toggleType, selection);
                    }}
                    selectable={true}
                    loading={isCollectionLoading}
                    headings={[
                        { title: 'Image' },
                        { title: 'ID' },
                        { title: 'Title' },
                        { title: 'Product count' },
                        { title: 'Action' }
                    ]}
                    pagination={{
                        hasNext: currentPage < totalCollectionsCount / limit,
                        onNext: handleNext,
                        label: `${(currentPage - 1) * limit + 1}–${Math.min(
                            currentPage * limit,
                            totalCollectionsCount
                        )} of ${totalCollectionsCount} collections`,
                        hasPrevious: currentPage > 1,
                        onPrevious: handlePrevious,
                    }}
                >
                    {rowMarkup}
                </IndexTable>
            </div>
        </React.Fragment>
    );
};

export default CollectionTable;